import {
  isSongTypeDisplayLabel,
  validateKkOriginalHints,
} from "./uploadMetadataHints";

/** Canonical catalog artist for every KK Original upload. */
export const KK_ORIGINAL_ARTIST_NAME = "KrossKast";

export type KkOriginalFilenameParse = {
  title: string | null;
  /** Artist segment from filename when present — audit only; never catalog identity. */
  filenameArtist: string | null;
  matchedDash: boolean;
};

/**
 * Extract song title from a KK Original MP3 filename.
 *
 * Supported patterns:
 * - `KrossKast - 3AM Somewhere.mp3` → `3AM Somewhere`
 * - `3AM Somewhere.mp3` → `3AM Somewhere`
 * - Title may contain additional hyphens after the first ` - ` separator
 *
 * Filename artist segment is returned for audit only. Catalog artist is always
 * {@link KK_ORIGINAL_ARTIST_NAME}.
 */
export function parseKkOriginalFilename(
  originalFilename: string,
): KkOriginalFilenameParse {
  const result: KkOriginalFilenameParse = {
    title: null,
    filenameArtist: null,
    matchedDash: false,
  };
  if (typeof originalFilename !== "string" || !originalFilename.trim()) {
    return result;
  }

  const trimmed = originalFilename.trim();
  const base = trimmed.replace(/\.[^.\\/]+$/i, "").trim();
  if (!base) return result;

  const dashSplit = base.split(/\s+[-–—]\s+/);
  if (dashSplit.length >= 2) {
    result.matchedDash = true;
    result.filenameArtist = dashSplit[0]?.trim() || null;
    const title = dashSplit.slice(1).join(" - ").trim();
    result.title = title || null;
  } else {
    result.title = base;
  }

  if (result.title && isSongTypeDisplayLabel(result.title)) {
    result.title = null;
  }
  if (result.filenameArtist && isSongTypeDisplayLabel(result.filenameArtist)) {
    result.filenameArtist = null;
  }

  return result;
}

export type ResolveKkOriginalHintsResult =
  | { ok: true; title: string; artistName: typeof KK_ORIGINAL_ARTIST_NAME }
  | { ok: false; message: string };

/**
 * Resolve upload hints for a KK Original file.
 * Artist is always KrossKast. Title comes from the filename (per-file).
 */
export function resolveKkOriginalUploadHints(
  originalFilename: string,
): ResolveKkOriginalHintsResult {
  const parsed = parseKkOriginalFilename(originalFilename);
  if (!parsed.title) {
    return {
      ok: false,
      message:
        'Could not determine song title from filename. Use "KrossKast - Song Title.mp3" or "Song Title.mp3".',
    };
  }

  const hintError = validateKkOriginalHints(parsed.title, KK_ORIGINAL_ARTIST_NAME);
  if (hintError) {
    return { ok: false, message: hintError };
  }

  return {
    ok: true,
    title: parsed.title,
    artistName: KK_ORIGINAL_ARTIST_NAME,
  };
}
